import { Component, useContext, useEffect, useMemo, useState } from "react";

import {
  INSURANCE_PLAN_FAMILY_CROP,
  INSURANCE_PLAN_NAME_APH,
  INSURANCE_PLAN_NAME_ECO_RP,
  INSURANCE_PLAN_NAME_ECO_RP_HPE,
  INSURANCE_PLAN_NAME_ECO_YP,
  INSURANCE_PLAN_NAME_RP,
  INSURANCE_PLAN_NAME_RP_HPE,
  INSURANCE_PLAN_NAME_SCO_RP,
  INSURANCE_PLAN_NAME_SCO_RP_HPE,
  INSURANCE_PLAN_NAME_SCO_YP,
  INSURANCE_PLAN_NAME_STAX_RP,
  INSURANCE_PLAN_NAME_STAX_RP_HPE,
  INSURANCE_PLAN_NAME_YP,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { optimizeInsurance } from "@datanac/datanac-api-toolkit/dist/insurance/optimizer/optimizer";
import { selectCoverageLevels } from "@datanac/datanac-api-toolkit/dist/insurance/optimizer/optimizerHelper";
import {
  getInsurancePlanNameAbbreviation,
  getInsurancePlansByFamily,
} from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { CloseOutlined, EastOutlined, WestOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { buildMcoComboRows } from "api-toolkit/mco/buildMcoComboRows";
import { buildMcoSupplementPlanOptions } from "api-toolkit/mco/buildMcoSupplementPlanOptions";
import { loadAreaSupplementOffers, UNDERLYING_REQUIREMENT_DISCLAIMER } from "api-toolkit/mco/loadAreaSupplementOffers";
import { ApiHelper, RmaApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { InsuranceIcon } from "components/Icons/Icons";
import { ButtonLargeSquare } from "mui-toolkit/ButtonLargeSquare";
import { useSnackbar } from "notistack";

import InsuranceCalculatePremiumEndorsementsTableGrid from "./InsuranceCalculatePremiumEndorsementsTableGrid";
import InsuranceCalculatePremiumTableGrid from "./InsuranceCalculatePremiumTableGrid";
import InsurancePlanCard from "./InsurancePlanCard";
import McoUnderlyingCoverageStep from "./McoUnderlyingCoverageStep";

export const AddInsuranceDialog = ({
  farmParameters,
  countyParameters,

  onClose,
  onSelectPlan,
}) => {
  const { globalState } = useContext(AppContext);

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const isLg = useMediaQuery("(min-width:1440px)");

  const { enqueueSnackbar } = useSnackbar();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  // --- --- ---

  const [selectedInsuranceOfferCrop, setSelectedInsuranceOfferCrop] = useState(null);

  const [selectedInsurancePlanCrop, setSelectedInsurancePlanCrop] = useState(null);
  const [selectedInsurancePlans, setSelectedInsurancePlans] = useState(null);

  const [insuranceOffers, setInsuranceOffers] = useState([]);
  const [insuranceOffersCropPlans, setInsuranceOffersCropPlans] = useState([]);
  const [insuranceOffersEndorsements, setInsuranceOffersEndorsements] = useState([]);
  const [offersLoaded, setOffersLoaded] = useState(false);
  const [areaSupplementOffers, setAreaSupplementOffers] = useState([]);
  const [loadingAreaSupplements, setLoadingAreaSupplements] = useState(false);
  const [supplementOnlyMode, setSupplementOnlyMode] = useState(false);
  const [selectedSupplementOffer, setSelectedSupplementOffer] = useState(null);
  const [underlyingCoverageLevel, setUnderlyingCoverageLevel] = useState(null);
  const [mcoSupplementLoading, setMcoSupplementLoading] = useState(false);
  const [mcoSupplementError, setMcoSupplementError] = useState(null);

  const offerGeoParams = useMemo(() => {
    if (countyParameters) return countyParameters;
    if (!farmParameters) return null;
    return {
      reinsurance_year: farmParameters.reinsurance_year ?? farmParameters.year,
      state_name: farmParameters.state_name,
      county_name: farmParameters.county_name,
      commodity_name: farmParameters.commodity_name,
      type_name: farmParameters.type_name,
      practice_name: farmParameters.practice_name,
    };
  }, [countyParameters, farmParameters]);

  const loadInsuranceOffers = async () => {
    if (!offerGeoParams) return;
    setOffersLoaded(false);
    try {
      const _insuranceOffers = await RmaApiHelper.selectInsuranceOffer(offerGeoParams, globalState);
      setInsuranceOffers(_insuranceOffers?.length ? _insuranceOffers : []);
    } catch (error) {
      console.warn("loadInsuranceOffers", error);
      setInsuranceOffers([]);
    } finally {
      setOffersLoaded(true);
    }
  };

  useEffect(() => {
    if (offerGeoParams) {
      loadInsuranceOffers();
    }
  }, [offerGeoParams]);

  const _cropPlanNames = [...getInsurancePlansByFamily(INSURANCE_PLAN_FAMILY_CROP), INSURANCE_PLAN_NAME_APH];

  useEffect(() => {
    const _insuranceOffersCropPlans = (insuranceOffers || []).filter((io) =>
      _cropPlanNames.includes(io?.insurance_plan_name)
    );
    setInsuranceOffersCropPlans(_insuranceOffersCropPlans);
  }, [insuranceOffers]);

  useEffect(() => {
    if (!offersLoaded || insuranceOffersCropPlans.length > 0 || !farmParameters) {
      if (insuranceOffersCropPlans.length > 0) setAreaSupplementOffers([]);
      return;
    }

    let cancelled = false;
    setLoadingAreaSupplements(true);
    loadAreaSupplementOffers({ farmParameters, countyParameters: offerGeoParams, insuranceOffers })
      .then((offers) => {
        if (!cancelled) setAreaSupplementOffers(offers || []);
      })
      .catch((error) => {
        console.warn("loadAreaSupplementOffers", error);
        if (!cancelled) setAreaSupplementOffers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAreaSupplements(false);
      });

    return () => {
      cancelled = true;
    };
  }, [offersLoaded, insuranceOffersCropPlans.length, farmParameters, offerGeoParams, insuranceOffers]);

  useEffect(() => {
    if (insuranceOffers?.length && selectedInsuranceOfferCrop) {
      const _insuranceOffersSupplementPlans = insuranceOffers.filter(
        (io) =>
          !_cropPlanNames.includes(io?.insurance_plan_name) &&
          ((selectedInsuranceOfferCrop?.insurance_plan_name === INSURANCE_PLAN_NAME_RP &&
            [INSURANCE_PLAN_NAME_ECO_RP, INSURANCE_PLAN_NAME_SCO_RP, INSURANCE_PLAN_NAME_STAX_RP].includes(
              io.insurance_plan_name
            )) ||
            (selectedInsuranceOfferCrop?.insurance_plan_name === INSURANCE_PLAN_NAME_RP_HPE &&
              [
                INSURANCE_PLAN_NAME_ECO_RP_HPE,
                INSURANCE_PLAN_NAME_SCO_RP_HPE,
                INSURANCE_PLAN_NAME_STAX_RP_HPE,
              ].includes(io.insurance_plan_name)) ||
            (selectedInsuranceOfferCrop?.insurance_plan_name === INSURANCE_PLAN_NAME_YP &&
              [INSURANCE_PLAN_NAME_ECO_YP, INSURANCE_PLAN_NAME_SCO_YP].includes(io.insurance_plan_name)))
      );
      setInsuranceOffersEndorsements(_insuranceOffersSupplementPlans);
    }
  }, [insuranceOffers?.length, selectedInsuranceOfferCrop]);

  const handleInsuranceOfferClick = (insuranceOffer) => {
    setSupplementOnlyMode(false);
    setSelectedInsuranceOfferCrop(insuranceOffer);
    handleNext();
  };

  const handleAreaSupplementClick = (supplementOffer) => {
    setSupplementOnlyMode(true);
    setSelectedSupplementOffer(supplementOffer);
    setUnderlyingCoverageLevel(null);
    setMcoSupplementError(null);
    setSelectedInsurancePlanCrop(null);
    setSelectedInsuranceOfferCrop({
      insurance_plan_name: supplementOffer.basePlanName,
      practice_name: supplementOffer.practice_name,
    });
    setEndorsementsInsurancePlanCombos([]);
    setActiveStep(1);
  };

  const handleUnderlyingCoverageSelect = (level) => {
    setUnderlyingCoverageLevel(level);
    handleNext();
  };

  // --- --- ---

  const [calculatePremiumsTableIsLoading, setCalculatePremiumsTableIsLoading] = useState(false);
  const [calculatePremiumsTableResult, setCalculatePremiumsTableResult] = useState([]);

  const bindCalculatePremiumsTableAsync = async () => {
    // Use ACREAGE from BUDGET
    // NOTE: the APH will be properly weighted according to Farm Vault / Fields,
    // and PREMIUMS will be properly calculated individually & weighted,
    // But we must allow the BUDGET acreage to override:
    const _reported_acreage_vault = farmParameters?.producerFarmFieldCrops?.reduce(
      (partialSum, pfc) => partialSum + pfc.reported_acreage,
      0
    );
    const _reported_acreage_budget = farmParameters?.reported_acreage;

    const parameters = {
      ...farmParameters,
      ...countyParameters,
      ...selectedInsuranceOfferCrop,
      reinsurance_year: countyParameters.reinsurance_year,
      insured_share_percent: farmParameters?.share_percent,

      reported_acreage: _reported_acreage_budget,
      reported_acreage_vault: _reported_acreage_vault,
      reported_acreage_budget: _reported_acreage_budget,
    };

    //console.log("InsurancePlanCard: premiums??");
    if (
      //parameters?.insurance_plan_name
      parameters?.reinsurance_year &&
      parameters?.commodity_name &&
      parameters?.type_name &&
      parameters?.practice_name &&
      parameters?.state_name &&
      parameters?.county_name &&
      parameters?.reported_acreage &&
      parameters?.approved_yield &&
      parameters?.insured_share_percent &&
      parameters?.projected_price &&
      parameters?.price_volatility_factor
    ) {
      setCalculatePremiumsTableIsLoading(true);

      const _calculatePremiumsTableResults = await RmaApiHelper.insurance_crop_CalculatePremiumsTable(parameters);
      if (_calculatePremiumsTableResults?.length) {
        setCalculatePremiumsTableResult(_calculatePremiumsTableResults);
        setCalculatePremiumsTableIsLoading(false);
      } else {
        setCalculatePremiumsTableResult([]);
        setCalculatePremiumsTableIsLoading(false);
      }
    } else {
      console.warn(
        "bindCalculatePremiumsTableAsync / not ready",
        parameters.projected_price,
        parameters.price_volatility_factor,
        parameters?.insured_share_percent
      );
    }
  };

  useEffect(() => {
    if (supplementOnlyMode || !farmParameters || !countyParameters || !selectedInsuranceOfferCrop) return;
    bindCalculatePremiumsTableAsync().catch((error) => {
      console.warn("bindCalculatePremiumsTableAsync", error);
    });
  }, [farmParameters, countyParameters, selectedInsuranceOfferCrop, supplementOnlyMode]);

  const handleCalculatePremiumTableSelection = (_insurancePlan) => {
    setSelectedInsurancePlanCrop(_insurancePlan);
    setSelectedInsurancePlans([_insurancePlan]);
    handleNext();
  };

  // --- --- ---

  const [endorsementsInsurancePlanCombos, setEndorsementsInsurancePlanCombos] = useState([]);
  const [endorsementsError, setEndorsementsError] = useState(null);

  const setEndorsementCombos = async (_endorsementCombos) => {
    setEndorsementsInsurancePlanCombos(_endorsementCombos);

    try {
      const _mcoRows = await buildMcoComboRows({
        baseCombos: _endorsementCombos,
        farmParameters,
        countyParameters,
        basePlanName: selectedInsuranceOfferCrop?.insurance_plan_name,
      });
      if (_mcoRows?.length) {
        setEndorsementsInsurancePlanCombos([..._endorsementCombos, ..._mcoRows]);
      }
    } catch (error) {
      console.warn("buildMcoComboRows", error);
    }
  };

  useEffect(() => {
    if (
      !supplementOnlyMode ||
      !farmParameters ||
      !offerGeoParams ||
      !selectedSupplementOffer ||
      underlyingCoverageLevel == null
    ) {
      return;
    }

    let cancelled = false;
    setMcoSupplementLoading(true);
    setMcoSupplementError(null);
    setEndorsementsInsurancePlanCombos([]);

    buildMcoSupplementPlanOptions({
      farmParameters: {
        ...farmParameters,
        practice_name: selectedSupplementOffer.practice_name || farmParameters?.practice_name,
      },
      countyParameters: offerGeoParams,
      supplementOffer: selectedSupplementOffer,
      underlyingCoverage: underlyingCoverageLevel,
    })
      .then(({ rows, blockers }) => {
        if (cancelled) return;
        setEndorsementsInsurancePlanCombos(rows || []);
        if (!rows?.length) {
          setMcoSupplementError(blockers?.join("; ") || "No MCO margin coverage options are available yet.");
        }
      })
      .catch((error) => {
        console.warn("buildMcoSupplementPlanOptions", error);
        if (!cancelled) {
          setMcoSupplementError(error?.message || "Could not load MCO options");
          setEndorsementsInsurancePlanCombos([]);
        }
      })
      .finally(() => {
        if (!cancelled) setMcoSupplementLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [supplementOnlyMode, farmParameters, offerGeoParams, selectedSupplementOffer, underlyingCoverageLevel]);

  const bindCalculateEndorsementsPremiumsTableAsync = async () => {
    setEndorsementsError(null);
    const endorsementOptimizerConstraints = {
      reinsurance_year: countyParameters.reinsurance_year,
      unit_structure_code: selectedInsurancePlanCrop?.unit_structure_code,
      insurance_plan_filter: {
        insurance_plan_names: [
          selectedInsuranceOfferCrop?.insurance_plan_name,
          INSURANCE_PLAN_NAME_ECO_RP,
          INSURANCE_PLAN_NAME_ECO_RP_HPE,
          INSURANCE_PLAN_NAME_ECO_YP,
          INSURANCE_PLAN_NAME_SCO_RP,
          INSURANCE_PLAN_NAME_SCO_RP_HPE,
          INSURANCE_PLAN_NAME_SCO_YP,
          INSURANCE_PLAN_NAME_STAX_RP,
          INSURANCE_PLAN_NAME_STAX_RP_HPE,
        ],
        coverage_level_percents: [selectedInsurancePlanCrop?.coverage_level_percent],
      },
    };

    const _filteredMyFarms = [farmParameters];
    _filteredMyFarms.forEach((f) => {
      // f.reinsurance_year = budgetCountyPractice?.year;
      f.insured_share_percent = f.share_percent;
      f.location_state_name = f.state_name;
      f.location_county_name = f.county_name;
      // f.production_cost_per_acre = budgetCountyPractice?.production_cost_per_acre;
    });

    const _optimize = (constraints) =>
      optimizeInsurance({
        myFarms: _filteredMyFarms,
        countyData: countyParameters,
        constraints,
        apiHelper: ApiHelper,
        onError: handleError,
      });

    try {
      const _endorsementCombos = await _optimize(endorsementOptimizerConstraints);
      if (_endorsementCombos?.length) {
        await setEndorsementCombos(_endorsementCombos);
      } else {
        setEndorsementsError("No optional policy endorsements are available for this coverage.");
      }
    } catch (error) {
      const _basePlanName = selectedInsuranceOfferCrop?.insurance_plan_name;
      const _baseCoverage = selectedInsurancePlanCrop?.coverage_level_percent;
      const _planParameters = (combo) =>
        combo?.comboStressTestResults?.insurancePlans?.map((plan) => plan?.insurancePlanParameters || plan) || [];
      const _hasPlan = (combo, planName, coverage) =>
        _planParameters(combo).some(
          (plan) =>
            plan?.insurance_plan_name === planName &&
            (coverage === undefined || coverage === null || Number(plan?.coverage_level_percent) === Number(coverage))
        );
      const _hasOfferedPlan = (planName) =>
        insuranceOffersEndorsements.some((offer) => offer.insurance_plan_name === planName);
      const _recoveryRequests = [];

      for (const _ecoPlanName of [
        INSURANCE_PLAN_NAME_ECO_RP,
        INSURANCE_PLAN_NAME_ECO_RP_HPE,
        INSURANCE_PLAN_NAME_ECO_YP,
      ].filter(_hasOfferedPlan)) {
        for (const _ecoCoverage of await selectCoverageLevels({ insurance_plan_name: _ecoPlanName })) {
          _recoveryRequests.push(
            _optimize({
              ...endorsementOptimizerConstraints,
              insurance_plan_filter: {
                insurance_plan_names: [_basePlanName, _ecoPlanName],
                coverage_level_percents: [_ecoCoverage],
              },
            }).then((combos) =>
              combos.filter(
                (combo) => _hasPlan(combo, _basePlanName, _baseCoverage) && _hasPlan(combo, _ecoPlanName, _ecoCoverage)
              )
            )
          );
        }
      }

      for (const _scoPlanName of [
        INSURANCE_PLAN_NAME_SCO_RP,
        INSURANCE_PLAN_NAME_SCO_RP_HPE,
        INSURANCE_PLAN_NAME_SCO_YP,
      ].filter(_hasOfferedPlan)) {
        _recoveryRequests.push(
          _optimize({
            ...endorsementOptimizerConstraints,
            insurance_plan_filter: {
              insurance_plan_names: [_basePlanName, _scoPlanName],
              coverage_level_percents: [_baseCoverage],
            },
          }).then((combos) =>
            combos.filter((combo) => _hasPlan(combo, _basePlanName, _baseCoverage) && _hasPlan(combo, _scoPlanName))
          )
        );
      }

      const _recoveredCombos = (await Promise.allSettled(_recoveryRequests)).flatMap((result) =>
        result.status === "fulfilled" ? result.value : []
      );
      if (_recoveredCombos.length) {
        await setEndorsementCombos(_recoveredCombos);
      } else {
        setEndorsementsError(error?.detail || error?.message || String(error));
        handleError(error);
      }
    }
  };

  useEffect(() => {
    if (supplementOnlyMode) return;
    if (!farmParameters || !countyParameters || !selectedInsuranceOfferCrop || !selectedInsurancePlanCrop) return;
    bindCalculateEndorsementsPremiumsTableAsync();
  }, [farmParameters, countyParameters, selectedInsuranceOfferCrop, selectedInsurancePlanCrop, supplementOnlyMode]);

  const handleEndorsementAdd = (_insurancePlans) => {
    setSelectedInsurancePlans(_insurancePlans);
    handleNext();
  };

  // --- --- ---

  const STEP_KEY_INSURANCE_PLAN_FAMILY = "insurance_plan_family";
  const STEP_KEY_COVERAGE = "coverage";
  const STEP_KEY_ENDORSEMENTS = "endorsements";
  const STEP_KEY_REVIEW = "review";

  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    {
      label: "Insurance Plan",
      description: "Select a base crop plan",
      key: STEP_KEY_INSURANCE_PLAN_FAMILY,
      isValid: () => {
        return true;
      },
    },
    {
      label: "Coverage",
      description: "",
      key: STEP_KEY_COVERAGE,
      isValid: () => {
        return true;
      },
    },
    {
      label: "Endorsements",
      description: "",
      key: STEP_KEY_ENDORSEMENTS,
      isValid: () => {
        return true;
      },
    },
    {
      label: "Review",
      description: "",
      key: STEP_KEY_REVIEW,
      isValid: () => {
        return true;
      },
    },
  ];

  class HorizontalStepContent extends Component {
    render() {
      const thisStep = this?.props?.step;

      return <>{steps[activeStep]?.key === thisStep?.key && <Typography>{thisStep?.description}</Typography>}</>;
    }
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (supplementOnlyMode && activeStep === 2) {
      setUnderlyingCoverageLevel(null);
      setEndorsementsInsurancePlanCombos([]);
      setMcoSupplementError(null);
      setActiveStep(1);
      return;
    }
    if (supplementOnlyMode && activeStep === 1) {
      setSupplementOnlyMode(false);
      setSelectedSupplementOffer(null);
      setSelectedInsuranceOfferCrop(null);
      setSelectedInsurancePlans(null);
      setUnderlyingCoverageLevel(null);
      setEndorsementsInsurancePlanCombos([]);
      setMcoSupplementError(null);
      setActiveStep(0);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSelectPlan = () => {
    onSelectPlan && onSelectPlan(selectedInsurancePlans);
    onClose();
  };

  // --- --- ---

  return (
    <>
      <DialogTitle>Add Plan</DialogTitle>

      <DialogContent>
        {steps[activeStep].key === STEP_KEY_INSURANCE_PLAN_FAMILY && (
          <>
            {insuranceOffersCropPlans?.length > 0 ? (
              <>
                <Typography>Select an insurance plan type:</Typography>
                <Stack
                  direction={isSmall ? "column" : "row"}
                  spacing={2}
                  alignItems="stretch"
                  flexWrap="wrap"
                  justifyContent="space-around"
                  sx={{ mt: isSmall ? "4px" : "50px" }}
                >
                  {insuranceOffersCropPlans.map((io, index) => (
                    <Box key={STEP_KEY_INSURANCE_PLAN_FAMILY + "/" + index}>
                      <ButtonLargeSquare
                        sx={{ width: "150pt" }}
                        icon={<InsuranceIcon fontSize="large" />}
                        title={getInsurancePlanNameAbbreviation(io)}
                        onClick={() => handleInsuranceOfferClick(io)}
                        subHeader={io?.insurance_plan_name}
                      />
                    </Box>
                  ))}
                </Stack>
              </>
            ) : (
              <>
                {!offersLoaded || loadingAreaSupplements ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Checking available area supplements…</Typography>
                  </Stack>
                ) : areaSupplementOffers?.length ? (
                  <>
                    <Typography>No base crop plans are available yet. Select an area supplement:</Typography>
                    <Stack
                      direction={isSmall ? "column" : "row"}
                      spacing={2}
                      alignItems="stretch"
                      flexWrap="wrap"
                      justifyContent="space-around"
                      sx={{ mt: isSmall ? "4px" : "50px" }}
                    >
                      {areaSupplementOffers.map((offer, index) => (
                        <Box key={STEP_KEY_INSURANCE_PLAN_FAMILY + "/supp/" + index}>
                          <ButtonLargeSquare
                            sx={{ width: "150pt" }}
                            icon={<InsuranceIcon fontSize="large" />}
                            title={offer.label}
                            onClick={() => handleAreaSupplementClick(offer)}
                            subHeader={offer.subLabel}
                            dataTestid={`area-supplement-${offer.planCode}`}
                          />
                        </Box>
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                      {UNDERLYING_REQUIREMENT_DISCLAIMER}
                    </Typography>
                  </>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No insurance plans or area supplements are available for this county and crop yet.
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        {steps[activeStep].key === STEP_KEY_COVERAGE && (
          <>
            {supplementOnlyMode ? (
              <>
                <McoUnderlyingCoverageStep
                  basePlanName={selectedSupplementOffer?.basePlanName}
                  selectedCoverage={underlyingCoverageLevel}
                  onSelectCoverage={handleUnderlyingCoverageSelect}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                  {UNDERLYING_REQUIREMENT_DISCLAIMER}
                </Typography>
              </>
            ) : (
              <>
                <Typography>Select a coverage level and unit structure:</Typography>

                {Boolean(countyParameters?.is_prior_year) && (
                  <Alert severity="warning">Prior year data used for insurance prices.</Alert>
                )}
                <InsuranceCalculatePremiumTableGrid
                  farmParameters={farmParameters}
                  insuranceOffer={selectedInsuranceOfferCrop}
                  calculatePremiumsTableResult={calculatePremiumsTableResult}
                  onPremiumPriceSelect={handleCalculatePremiumTableSelection}
                />
              </>
            )}
          </>
        )}

        {steps[activeStep].key === STEP_KEY_ENDORSEMENTS && (
          <>
            <Typography>
              {supplementOnlyMode
                ? "Select a margin coverage option:"
                : "Review & select optional policy endorsements:"}
            </Typography>
            {supplementOnlyMode && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {UNDERLYING_REQUIREMENT_DISCLAIMER}
              </Typography>
            )}
            {supplementOnlyMode && underlyingCoverageLevel != null && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Intended underlying {selectedSupplementOffer?.basePlanName} at{" "}
                {Math.round(underlyingCoverageLevel * 100)}% (not saved — add RP separately when on sale). Select a
                margin coverage band:
              </Typography>
            )}
            <InsuranceCalculatePremiumEndorsementsTableGrid
              farmParameters={farmParameters}
              optimizerResults={endorsementsInsurancePlanCombos}
              onInsurancePlansSelect={handleEndorsementAdd}
              isLoading={
                supplementOnlyMode
                  ? mcoSupplementLoading
                  : !endorsementsInsurancePlanCombos?.length && !endorsementsError
              }
              emptyMessage={mcoSupplementError || endorsementsError}
            />
          </>
        )}

        {steps[activeStep].key === STEP_KEY_REVIEW && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton">
              <Typography variant="h1">Selected Insurance Plans</Typography>
            </Stack>
            <Box className="cardContainer">
              {Boolean(selectedInsurancePlans?.length) &&
                selectedInsurancePlans?.map((currentPlan, index) => (
                  <InsurancePlanCard key={index} countyPractice={farmParameters} insurancePlan={currentPlan} />
                ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!isSmall && (
          <Stepper activeStep={activeStep} orientation="horizontal" sx={{ flexGrow: "1", mr: isLg ? "200px" : 0 }}>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel optional={null}>{step.label || ""}</StepLabel>
                <HorizontalStepContent step={step}>
                  <Typography>{step.description}</Typography>
                </HorizontalStepContent>
              </Step>
            ))}
          </Stepper>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="contained" color="secondary" autoFocus onClick={onClose} sx={{ mr: { xs: 0, lg: 5 } }}>
            <CloseOutlined sx={{ mr: 1 }} />
            Cancel
          </Button>

          <Button variant="contained" color="primary" disabled={activeStep === 0} onClick={handleBack}>
            <WestOutlined sx={{ mr: 1 }} />
            Back
          </Button>

          {/* {!(steps[activeStep].key == STEP_KEY_REVIEW) &&
                    <Button
                        variant="contained" color="primary"
                        onClick={handleNext}
                        disabled={!steps[activeStep].isValid()}
                    >
                        {activeStep === steps.length - 1 ? 'Next' : 'Next'}
                        <EastOutlined sx={{ ml: 1 }} />
                    </Button>} */}

          {steps[activeStep].key === STEP_KEY_REVIEW && (
            <>
              <Button variant="contained" onClick={handleSelectPlan}>
                Select Plan <EastOutlined sx={{ ml: 1 }} />
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </>
  );
};
